import { createFileRoute } from "@tanstack/react-router";
import type { RouterContext } from "@/types/router";
import { DisciplineDetail } from "@/components/discipline-detail";
import { DisciplineDetailSkeleton } from "@/components/discipline-detail/discipline-detail-skeleton";
import { disciplineQueryOptions } from "@/queries/use-disciplines";
import { useTranslation } from "react-i18next";
import { ResourceError } from "@/components/ui/resource-error";
import { ScrollableContainer } from "@/components/ui/scrollbar";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";

interface LoaderParams {
  params: { disciplineId: string };
  context: RouterContext;
}

async function loadDisciplineData({ params, context }: LoaderParams) {
  const discipline = await context.queryClient.ensureQueryData(
    disciplineQueryOptions(params.disciplineId),
  );

  return {
    breadcrumb: discipline?.name,
    hasActionButtons: true,
  };
}

function ErrorComponent({ error }: { error: Error }) {
  const { t } = useTranslation();

  return (
    <div className="flex h-full flex-col px-6 md:px-6 lg:my-2">
      <ResourceError
        error={error}
        notFoundMessage={t("pages:discipline.errors.notFound")}
        errorMessage={t("pages:discipline.errors.loadingFailed", {
          message: error.message,
        })}
      />
    </div>
  );
}

export const Route = createFileRoute(
  "/(protected)/management/disciplines/$disciplineId/",
)({
  loader: loadDisciplineData,
  pendingComponent: () => <DisciplineDetailSkeleton />,
  component: RouteComponent,
  errorComponent: ErrorComponent,
});

function RouteComponent() {
  const { disciplineId } = Route.useParams();
  const loaderData = Route.useLoaderData();

  return (
    <ScrollableContainer className="mt-0">
      <div className="flex min-h-full flex-col px-6 md:px-6 lg:py-2">
        <MobilePageTitle className="mt-6 mb-4">
          {loaderData.breadcrumb}
        </MobilePageTitle>
        <DisciplineDetail disciplineId={disciplineId} />
      </div>
    </ScrollableContainer>
  );
}
